import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Colors from '../../../ui/global/Colors';
import Fonts from '../../../ui/global/Fonts';
import MenuPanel from '../../../ui/components/MenuPanel';
import { GithubFormat, localUntransformCode } from '../utils/formatCode';
import { extractBalanceConstants, applyBalanceConstants } from '../utils/useBalanceConstants';
import { extractVisualConstants, applyVisualConstants } from '../utils/useVisualConstants';
import InlineInput from '../../../ui/components/InlineInput';
import Button from '../../../ui/components/Button';
import Slider from '../../../ui/components/Slider';
import Tooltip from '../../../ui/components/Tooltip';
import ColorSwatch from '../../../ui/components/ColorSwatch';
import ColorPicker from '../../../ui/components/ColorPicker';
import Toggle from '../../../ui/components/Toggle';
import { useConfig } from '../../../utils/config';
import { useMediaQuery } from '@mui/material';
import { useSharedPanelState } from './panels/SharedPanelState';

const Inner = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    min-height: 100%;
    margin: auto;
    line-height: 1.2em;
    gap: 20px;
    padding-bottom: 40px;
`;

const Title = styled.h4`
    margin: 0 auto;
    width: 100%;
    text-align: left;
    font-family: ${Fonts.lexendSemiBold.fontFamily};
    font-weight: ${Fonts.lexendSemiBold.fontWeight};
    color: ${Colors.grey1};
    padding: 4px 0 0 0;
`;

const Subtitle = styled.h6`
    margin: 0 auto;
    width: 100%;
    text-align: left;
    font-family: ${Fonts.lexendMedium.fontFamily};
    font-weight: ${Fonts.lexendMedium.fontWeight};
    color: ${Colors.grey5};
    font-size: 12px;
    text-transform: uppercase;
    padding: 0;
    margin: 0;
`;

const ParamatersContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    height: 100%;
    // margin: auto;
    gap: 8px;
    color: ${Colors.grey2};
    line-height: 1em;
`;

const Parameter = styled.div<{ $direction: 'row' | 'column' }>`
    position: relative;
    display: flex;
    flex-direction: ${props => props.$direction};
    align-items: ${props => props.$direction === 'row' ? 'center' : 'flex-start'};
    justify-content: space-between;
    width: 100%;
    font-size: 14px;
    gap: ${props => props.$direction === 'row' ? '8px' : '2px'};
    padding: ${props => props.$direction === 'row' ? '0' : '6px 0 0 0'}
`;

const Label = styled.label`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    font-family: ${Fonts.lexendSemiLight.fontFamily};
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
    flex: 1;
`;

const ButtonContainer = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 10px;
    padding: 2px 10px 5px 10px;
    // backdrop-filter: blur(8px);
    background-color: ${Colors.grey9};
    box-shadow: 0 -2px 5px ${Colors.grey9};
    box-sizing: border-box;
`;

const SectionDivider = styled.div`
    width: 100%;
    height: 0;
    border-top: 1px solid ${Colors.grey8};
`;

export interface Constant {
  label: string;
  value: string;
  type: 'number' | 'string' | 'color' | 'boolean';
  // Optional grouping metadata for array items
  groupKey?: string;
  groupIndex?: number;
}

interface GameSettingsPanelProps {
    code: GithubFormat;
    originalCode: GithubFormat;
    onConstantChange: (updatedCode: GithubFormat) => void;
    onSaveChanges: (updatedCode: GithubFormat) => void;
    mode: string;
    historyEntryId?: string;
    isGeneratingGame: boolean;
    isUpdatingGameArt: boolean;
    isViewingLatest: boolean;
    latestUserFacingPrompt?: string;
}

const GameSettingsPanel: React.FC<GameSettingsPanelProps> = ({ code, originalCode, onConstantChange, onSaveChanges, mode, historyEntryId, isGeneratingGame, isUpdatingGameArt, isViewingLatest, latestUserFacingPrompt }) => {
  const config = useConfig();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { isSettingsPanelOpen: isOpen, toggleSettingsPanel: setIsOpen, isAssetsPanelOpen } = useSharedPanelState();
  const [balanceConstants, setBalanceConstants] = useState<Constant[]>([]);
  const [visualConstants, setVisualConstants] = useState<Constant[]>([]);
  const [originalBalanceConstants, setOriginalBalanceConstants] = useState<Constant[]>([]);
  const [originalVisualConstants, setOriginalVisualConstants] = useState<Constant[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [badgeVisible, setBadgeVisible] = useState(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [openColorPickerIndex, setOpenColorPickerIndex] = useState<number | null>(null);
  const [openColorPickerType, setOpenColorPickerType] = useState<'balance' | 'visual' | null>(null);
  const anchorElRefs = useRef<(HTMLElement | null)[][]>([]);
  const prevOriginalBalanceRef = useRef<Constant[] | null>(null);
  const prevOriginalVisualRef = useRef<Constant[] | null>(null);
  const initializedRef = useRef(false);

  // Initialize anchorElRefs for balance and visual constants
  useEffect(() => {
    anchorElRefs.current[0] = Array(balanceConstants.length).fill(null);
    anchorElRefs.current[1] = Array(visualConstants.length).fill(null);
  }, [balanceConstants.length, visualConstants.length]);

  // Extract original balance constants on initial load
  useEffect(() => {
    // console.log('GameSettingsPanel: original code changed, extracting current constants.');
    const balanceConstants = extractBalanceConstants(originalCode);
    const visualConstants = extractVisualConstants(originalCode);
    // console.log('Original Balance Constants:', balanceConstants);
    // console.log('Original Visual Constants:', visualConstants);
    setOriginalBalanceConstants(balanceConstants);
    setOriginalVisualConstants(visualConstants);
  }, [originalCode]);

  const isUserInitiatedSettingsUpdate = (latestUserFacingPrompt || '').trim().toLowerCase() === 'update game settings';

  const hasMeaningfulChange = (prev: Constant[] | null, curr: Constant[]) => {
    if (!prev) return false;
    const toMap = (arr: Constant[]) => {
      const m = new Map<string, string>();
      arr.forEach(c => m.set(c.label, c.value));
      return m;
    };
    const p = toMap(prev);
    const c = toMap(curr);
    if (p.size !== c.size) return true;
    let changed = false;
    p.forEach((v, k) => {
      if (changed) return;
      if (!c.has(k)) {
        changed = true;
        return;
      }
      if (c.get(k) !== v) {
        changed = true;
      }
    });
    if (changed) return true;
    return false;
  };

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
    } else {
      const balanceChanged = hasMeaningfulChange(prevOriginalBalanceRef.current, originalBalanceConstants);
      const visualChanged = hasMeaningfulChange(prevOriginalVisualRef.current, originalVisualConstants);
      const shouldAutoOpen =
        config.AUTO_OPEN_BALANCE_PANEL_ON_CHANGE &&
        (!isMobile || !isAssetsPanelOpen) &&
        isViewingLatest &&
        !isUserInitiatedSettingsUpdate &&
        (balanceChanged || visualChanged);
      if (shouldAutoOpen && !isOpen) setIsOpen(true);
    }
    prevOriginalBalanceRef.current = originalBalanceConstants;
    prevOriginalVisualRef.current = originalVisualConstants;
  }, [originalBalanceConstants, originalVisualConstants, isViewingLatest, latestUserFacingPrompt, config.AUTO_OPEN_BALANCE_PANEL_ON_CHANGE, isOpen, setIsOpen]);

  // Extract current balance constants when code changes
  useEffect(() => {
    // console.log('GameSettingsPanel: code prop changed, extracting current constants.');
    const extractedBalance = extractBalanceConstants(code);
    const extractedVisual = extractVisualConstants(code);
    // console.log('Extracted Balance Constants:', extractedBalance);
    // console.log('Extracted Visual Constants:', extractedVisual);
    setBalanceConstants(extractedBalance);
    setVisualConstants(extractedVisual);
  }, [code]);

  // Close color picker when code changes
  useEffect(() => {
    handleColorPickerClose();
  }, [code]);

  // Check if constants are different from original constants
  useEffect(() => {
    const checkChanges = (current: Constant[], original: Constant[]) => {
      if (current.length !== original.length) {
        return true;
      }
      return current.some((constant, index) => {
        return constant.value !== original[index]?.value;
      });
    };
    
    const balanceHasChanges = checkChanges(balanceConstants, originalBalanceConstants);
    const visualHasChanges = checkChanges(visualConstants, originalVisualConstants);
    
    setHasChanges(balanceHasChanges || visualHasChanges);
  }, [balanceConstants, originalBalanceConstants, visualConstants, originalVisualConstants]);

  useEffect(() => {
    if (isOpen) {
      setBadgeVisible(false);
    }
  }, [isOpen]);

  const handleConstantChange = (type: 'balance' | 'visual', index: number, newValue: string) => { // Modified to accept newValue directly
    let newConstants;
    let setter;
    let applyFunction;

    if (type === 'balance') {
      newConstants = [...balanceConstants];
      setter = setBalanceConstants;
      applyFunction = applyBalanceConstants;
    } else { // type === 'visual'
      newConstants = [...visualConstants];
      setter = setVisualConstants;
      applyFunction = applyVisualConstants;
    }

    newConstants[index] = {
      ...newConstants[index],
      value: newValue
    };
    setter(newConstants);
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Apply changes after 1 second after stopping typing
    timeoutRef.current = setTimeout(() => {
      if (onConstantChange) {
        let updatedCode = code;
        if (type === 'balance') {
          updatedCode = applyFunction(updatedCode, newConstants);
        } else {
          updatedCode = applyBalanceConstants(updatedCode, balanceConstants); // Apply balance constants first to ensure they are up to date
          updatedCode = applyFunction(updatedCode, newConstants); // Then apply visual constants
        }
        onConstantChange(updatedCode);
      }
    }, 1000);
  };

  const handleReset = () => {
    // Reset to original balanceConstants and visualConstants from code
    setBalanceConstants([...originalBalanceConstants]);
    setVisualConstants([...originalVisualConstants]);
    
    // Apply the original constants to the code
    if (onConstantChange) {
      let updatedCode = applyBalanceConstants(code, originalBalanceConstants);
      updatedCode = applyVisualConstants(updatedCode, originalVisualConstants);
      onConstantChange(updatedCode);
    }
  };
  
  const handleSaveChanges = () => {
    if (onSaveChanges && hasChanges) {
      // Create a new history entry with updated code
      let updatedCode = applyBalanceConstants(code, balanceConstants);
      updatedCode = applyVisualConstants(updatedCode, visualConstants);
      
      // Untransform each file before saving
      const untransformedCode = updatedCode.map(file => localUntransformCode(file, true))
      .filter((item): item is Exclude<typeof item, null> => item !== null) // Filter out nulls
      .filter(item => !item.path.includes('tsconfig.json') && !item.path.includes('package.json') && !item.path.includes('package-lock.json'));
      
      onSaveChanges(untransformedCode);
      setHasChanges(false);
    }
  };

  const handleColorSwatchClick = (event: React.MouseEvent<HTMLElement>, index: number, type: 'balance' | 'visual') => {
    setOpenColorPickerIndex(index);
    setOpenColorPickerType(type);
  };

  const handleColorPickerClose = () => {
    setOpenColorPickerIndex(null);
    setOpenColorPickerType(null);
  };

  const renderParameter = (constant: Constant, index: number, type: 'balance' | 'visual') => {
    const isColorPickerOpen = openColorPickerIndex === index && openColorPickerType === type;
    const refArrayIndex = type === 'balance' ? 0 : 1;

    return (
      <Parameter 
        key={`param-${historyEntryId}-${type}-${index}`}
        $direction="row"
        ref={el => {
          if (el) {
            if (!anchorElRefs.current[refArrayIndex]) {
              anchorElRefs.current[refArrayIndex] = [];
            }
            anchorElRefs.current[refArrayIndex][index] = el;
          }
        }}
      >
        <Label>
            {constant.label}
        </Label>
        {constant.type === 'color' ? (
          <>
            <ColorSwatch 
              color={constant.value} 
              size={22}
              onClick={(e) => handleColorSwatchClick(e, index, type)} 
            />
            <ColorPicker
              color={constant.value}
              onChange={(newColor) => handleConstantChange(type, index, newColor)}
              label={constant.label}
              open={isColorPickerOpen}
              onOpenChange={handleColorPickerClose}
              anchorEl={anchorElRefs.current[refArrayIndex]?.[index]}
            />
          </>
        ) : constant.type === 'boolean' ? (
          <div style={{ margin: '-5px -7px -5px 0' }}>
            <Toggle
                checked={constant.value.toLowerCase() === 'true'}
                onChange={(checked) => handleConstantChange(type, index, checked ? 'true' : 'false')}
                colorMode="dark"
            />
          </div>
        ) : (
          <InlineInput
              key={`${historyEntryId}-${type}-${constant.label}`}
              colorMode="dark" 
              value={constant.value} 
              onChange={(event) => handleConstantChange(type, index, event.target.value)}
              onEnterPress={() => {}}
              includeStyling={true}
          />
        )}
      </Parameter>
    );
  };

  const renderParameterSlider = (index: number, label: string, value: string | number, onChange: (event: React.ChangeEvent<HTMLInputElement>) => void) => {
    return (
        <Parameter key={'test'} $direction="column">
            <Label>
                Parameter Name
            </Label>
            <Slider
                colorMode="dark" 
                value={20} 
                min={0}
                max={100}
                step={5}
                onChange={()=>{}} 
            />
        </Parameter>
    );
  };

  if (mode === 'Code') return null;

  return (
    <>
    {(balanceConstants?.length > 0 || visualConstants?.length > 0) && (
      <MenuPanel
          key={historyEntryId}
          open={isOpen}
          onOpenChange={setIsOpen}
          position={'right'}
          width={220}
          colorMode="dark"
          expandTooltip={!isOpen ? "Game Settings" : null}
          // badge={{
          //   content: balanceConstants.length,
          //   vertical: 'top',
          //   horizontal: 'right',
          //   invisible: !badgeVisible,
          //   showZero: false
          // }}
      >
          <Inner>
          {balanceConstants?.length > 0 && (
            <>
              <Title>Balance Settings</Title>
              <ParamatersContainer>
                  {balanceConstants.map((constant, index) => 
                      renderParameter(constant, index, 'balance') // Pass 'balance' type
                  )}
                  {/* {balanceConstants.map((constant, index) => 
                      renderParameterSlider(index, constant.label, constant.value, (event) => handleConstantChange(index, event))
                  )} */}
              </ParamatersContainer>
            </>
          )}
          {balanceConstants?.length > 0 && visualConstants?.length > 0 && (
            <SectionDivider />
          )}
          {visualConstants?.length > 0 && (
            <>
              <Title>Visual Settings</Title>
              {/* <Subtitle>Subtitle Text</Subtitle> */}
              <ParamatersContainer>
                  {visualConstants.map((constant, index) => 
                      renderParameter(constant, index, 'visual') // Pass 'visual' type
                  )}
              </ParamatersContainer>
            </>
          )}
          </Inner>
          <ButtonContainer>
              <Tooltip text="Reset" colorMode="dark" hide={!hasChanges}>
                <Button 
                    size="tiny" 
                    color="ghost white" 
                    icon="arrow-u-up-left" 
                    onClick={handleReset} 
                    disabled={!hasChanges}
                />
              </Tooltip>
              <Button 
                  size="tiny" 
                  color="white" 
                  text="SAVE"
                  // icon="check-slim" 
                  onClick={handleSaveChanges} 
                  disabled={!hasChanges || isGeneratingGame || isUpdatingGameArt}
              />
              <div style={{ width: '38px' }}></div>
          </ButtonContainer>
      </MenuPanel>
    )}
    </>
  );
};

export default GameSettingsPanel;